import { useEffect, useState } from "react";
import {
  listUploadHistory,
  getUploadHistoryDetail,
  downloadUploadHistoryCsv,
  UploadHistorySummary,
  UploadHistoryRow,
} from "../../../api/api-hook";

const BRAND_GREEN = "#023822";
const BRAND_GOLD  = "#C9A24D";

function formatDate(ms: number): string {
  return new Date(ms).toLocaleString("en-US", {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

interface Props {
  refreshKey?: number;
}

const UploadHistory = ({ refreshKey }: Props) => {
  const [records,   setRecords]   = useState<UploadHistorySummary[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [expanded,  setExpanded]  = useState<string | null>(null);
  const [detail,    setDetail]    = useState<{ [id: string]: UploadHistoryRow[] }>({});
  const [detailLoading, setDetailLoading] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    listUploadHistory()
      .then(setRecords)
      .catch(() => setError("Failed to load upload history."))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const toggleExpand = async (id: string) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (detail[id]) return;
    setDetailLoading(id);
    try {
      const rec = await getUploadHistoryDetail(id);
      setDetail((prev) => ({ ...prev, [id]: rec.results }));
    } catch {
      setDetail((prev) => ({ ...prev, [id]: [] }));
    } finally {
      setDetailLoading(null);
    }
  };

  return (
    <div style={{ padding: "2rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
        <i className="bi bi-clock-history" style={{ fontSize: "1.4rem", color: BRAND_GREEN }} />
        <h4 style={{ margin: 0, color: BRAND_GREEN, fontWeight: 700 }}>Upload History</h4>
      </div>

      {loading && <p className="text-muted">Loading…</p>}
      {error   && <div className="alert alert-danger">{error}</div>}

      {!loading && !error && records.length === 0 && (
        <div className="text-muted">No uploads recorded yet.</div>
      )}

      {!loading && records.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {records.map((rec) => {
            const isOpen = expanded === rec.id;
            const rows   = detail[rec.id];
            const failures = rows ? rows.filter((r) => !r.success) : [];
            const successes = rows ? rows.filter((r) => r.success) : [];

            return (
              <div
                key={rec.id}
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "#fff",
                }}
              >
                {/* Header row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    padding: "0.85rem 1rem",
                    cursor: "pointer",
                    background: isOpen ? "rgba(2,56,34,0.04)" : "#fff",
                  }}
                  onClick={() => toggleExpand(rec.id)}
                >
                  <i
                    className={`bi bi-chevron-${isOpen ? "down" : "right"}`}
                    style={{ color: BRAND_GREEN, fontSize: "0.8rem", flexShrink: 0 }}
                  />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: "0.9rem", color: BRAND_GREEN, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {rec.filename}
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "#6c757d", marginTop: 2 }}>
                      {formatDate(rec.uploadedAt)} · {rec.uploadedBy}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
                    <span className="badge" style={{ background: "rgba(25,135,84,0.12)", color: "#198754", fontWeight: 600, fontSize: "0.75rem" }}>
                      {rec.successRows} ok
                    </span>
                    {rec.failureRows > 0 && (
                      <span className="badge" style={{ background: "rgba(220,53,69,0.12)", color: "#dc3545", fontWeight: 600, fontSize: "0.75rem" }}>
                        {rec.failureRows} failed
                      </span>
                    )}
                    <span className="badge" style={{ background: "rgba(108,117,125,0.1)", color: "#6c757d", fontWeight: 600, fontSize: "0.75rem" }}>
                      {rec.totalRows} total
                    </span>
                  </div>

                  <button
                    className="btn btn-sm"
                    style={{ border: `1px solid ${BRAND_GOLD}`, color: BRAND_GOLD, fontSize: "0.75rem", flexShrink: 0 }}
                    onClick={(e) => { e.stopPropagation(); downloadUploadHistoryCsv(rec.id); }}
                    title="Download original CSV"
                  >
                    <i className="bi bi-download" /> CSV
                  </button>
                </div>

                {/* Expanded detail */}
                {isOpen && (
                  <div style={{ borderTop: "1px solid #dee2e6", padding: "1rem" }}>
                    {detailLoading === rec.id && <p className="text-muted small mb-0">Loading rows…</p>}

                    {rows && rows.length > 0 && (
                      <>
                        {failures.length > 0 && (
                          <div style={{ marginBottom: "1rem" }}>
                            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#dc3545", marginBottom: "0.4rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Failed rows ({failures.length})
                            </div>
                            <div style={{ overflowX: "auto" }}>
                              <table className="table table-sm" style={{ fontSize: "0.8rem", marginBottom: 0 }}>
                                <thead>
                                  <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Package</th>
                                    <th>Error</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {failures.map((r, i) => (
                                    <tr key={i} style={{ background: "rgba(220,53,69,0.04)" }}>
                                      <td>{r.name}</td>
                                      <td>{r.email}</td>
                                      <td>{r.package || "—"}</td>
                                      <td style={{ color: "#dc3545" }}>{r.error || "Unknown error"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {successes.length > 0 && (
                          <div>
                            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#198754", marginBottom: "0.4rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Successful rows ({successes.length})
                            </div>
                            <div style={{ overflowX: "auto" }}>
                              <table className="table table-sm" style={{ fontSize: "0.8rem", marginBottom: 0 }}>
                                <thead>
                                  <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Package</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {successes.map((r, i) => (
                                    <tr key={i}>
                                      <td>{r.name}</td>
                                      <td>{r.email}</td>
                                      <td>{r.package || "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {rows && rows.length === 0 && (
                      <p className="text-muted small mb-0">No row detail available.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UploadHistory;
